import {
  BILLIN_DEFAULT_TABLE_FILTERS,
  BILLIN_DEFAULT_WORKER_ID,
  BILLIN_SERVICE_ID,
} from "@/lib/billinConstants";
import { persistLastWorkspace } from "@/lib/lastWorkspaceStorage";

export const BILLIN_GO_ACTIONS = [
  "table",
  "app",
  "settings",
  "switch",
  "help",
  "request",
  /** Rich menu → งานเงิน > ทั้งหมด */
  "work-all",
  /** Rich menu → งานเงิน > Bill-Lin กำลังถือ */
  "work-waiting",
] as const;

export type BillinGoAction = (typeof BILLIN_GO_ACTIONS)[number];

export const BILLIN_SCROLL_TO_TRANSACTIONS = "transactions";
export const BILLIN_SCROLL_TO_RECEIVABLE = "receivable";
export const BILLIN_SCROLL_TO_GOOGLE_SHEETS = "google-sheets";

export type BillinScrollTarget =
  | typeof BILLIN_SCROLL_TO_TRANSACTIONS
  | typeof BILLIN_SCROLL_TO_RECEIVABLE
  | typeof BILLIN_SCROLL_TO_GOOGLE_SHEETS;

export type BillinDestinationOptions = {
  scrollTo?: BillinScrollTarget;
  workerId?: string;
};

const PENDING_SESSION_KEY = "aera_billin_go_pending";

export function isBillinGoAction(value: string | null | undefined): value is BillinGoAction {
  if (!value) return false;
  return (BILLIN_GO_ACTIONS as readonly string[]).includes(value);
}

export function setPendingBillinGoAction(action: BillinGoAction): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(PENDING_SESSION_KEY, action);
  } catch {
    // ignore
  }
}

export function consumePendingBillinGoAction(): BillinGoAction | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(PENDING_SESSION_KEY);
    sessionStorage.removeItem(PENDING_SESSION_KEY);
    return isBillinGoAction(raw) ? raw : null;
  } catch {
    return null;
  }
}

export type OrderWorkspaceRef = {
  serviceId?: string;
  workspaceId?: string;
  workspace?: { id?: string };
  service?: { id?: string };
};

export function orderServiceId(order: OrderWorkspaceRef): string {
  const direct = order.serviceId?.trim();
  if (direct) return direct;
  const nested = order.service?.id?.trim();
  return nested || "";
}

export function orderWorkspaceId(order: OrderWorkspaceRef): string {
  return (
    order.workspaceId?.trim() ||
    order.workspace?.id?.trim() ||
    ""
  );
}

export function isServiceHiredInWorkspace(
  orders: OrderWorkspaceRef[],
  serviceId: string,
  workspaceId: string,
): boolean {
  const sid = serviceId.trim();
  const wid = workspaceId.trim();
  if (!sid || !wid) return false;
  return orders.some(
    (o) => orderServiceId(o) === sid && orderWorkspaceId(o) === wid,
  );
}

/** Home URL after picking a workspace (Billin hire vs work). */
export function buildHomeHrefAfterWorkspacePick(
  workspaceId: string,
  serviceId: string,
  opts: { isHired: boolean; setting?: boolean },
): string {
  const sid = serviceId.trim();
  const wid = workspaceId.trim();
  const p = new URLSearchParams();
  p.set("workspaceId", wid);
  p.set("source", opts.isHired ? "order" : "marketplace");

  if (opts.isHired) {
    p.set("serviceId", sid);
    if (sid === BILLIN_SERVICE_ID) {
      p.set("workerId", BILLIN_DEFAULT_WORKER_ID);
      p.set("filter_sort", BILLIN_DEFAULT_TABLE_FILTERS.filter_sort);
      p.set("filter_dir", BILLIN_DEFAULT_TABLE_FILTERS.filter_dir);
      p.set("fw", "work");
    }
  }

  if (opts.setting) p.set("setting", "true");
  return `/?${p.toString()}`;
}

function billinTableQueryBase(): URLSearchParams {
  const p = new URLSearchParams();
  p.set("serviceId", BILLIN_SERVICE_ID);
  p.set("workerId", BILLIN_DEFAULT_WORKER_ID);
  p.set("filter_sort", BILLIN_DEFAULT_TABLE_FILTERS.filter_sort);
  p.set("filter_dir", BILLIN_DEFAULT_TABLE_FILTERS.filter_dir);
  return p;
}

/** Final destination after workspace is known (or for switch/help/request). */
export function buildBillinDestination(
  action: BillinGoAction,
  workspaceId: string | null,
  opts?: BillinDestinationOptions,
): string {
  switch (action) {
    case "switch":
      return "/?selectworkspace=true";
    case "help":
      return "/support";
    case "request":
      return "/support/new/issue";
    case "table": {
      if (!workspaceId?.trim()) {
        throw new Error("workspaceId required for table");
      }
      const p = billinTableQueryBase();
      p.set("workspaceId", workspaceId.trim());
      if (opts?.workerId?.trim()) {
        p.set("workerId", opts.workerId.trim());
      }
      return `/artifacts/table?${p.toString()}`;
    }
    case "app": {
      if (!workspaceId?.trim()) {
        throw new Error("workspaceId required for app");
      }
      const p = billinTableQueryBase();
      p.set("workspaceId", workspaceId.trim());
      p.set("source", "order");
      if (opts?.scrollTo) p.set("scrollTo", opts.scrollTo);
      if (opts?.workerId?.trim()) {
        p.set("workerId", opts.workerId.trim());
      }
      return `/?${p.toString()}`;
    }
    case "settings": {
      if (!workspaceId?.trim()) {
        throw new Error("workspaceId required for settings");
      }
      const p = billinTableQueryBase();
      p.set("workspaceId", workspaceId.trim());
      p.set("source", "order");
      p.set("setting", "true");
      if (opts?.workerId?.trim()) {
        p.set("workerId", opts.workerId.trim());
      }
      return `/?${p.toString()}`;
    }
    case "work-all":
    case "work-waiting": {
      if (!workspaceId?.trim()) {
        throw new Error(`workspaceId required for ${action}`);
      }
      const p = billinTableQueryBase();
      p.set("workspaceId", workspaceId.trim());
      p.set("source", "order");
      p.set("fw", "work");
      p.set("fwFilter", action === "work-all" ? "all" : "waiting");
      if (opts?.workerId?.trim()) {
        p.set("workerId", opts.workerId.trim());
      }
      return `/?${p.toString()}`;
    }
    default: {
      const _exhaustive: never = action;
      return _exhaustive;
    }
  }
}

/** After user picks a workspace from the select modal (pending /go action). */
export function continuePendingBillinGo(
  workspaceId: string,
  navigate: (href: string) => void,
): boolean {
  const pending = consumePendingBillinGoAction();
  if (!pending || pending === "switch" || pending === "help" || pending === "request") {
    return false;
  }
  persistLastWorkspace(workspaceId);
  navigate(buildBillinDestination(pending, workspaceId));
  return true;
}
